// ============================================================
// B229 (AI_147) — PROVENIÊNCIA DA ANÁLISE (Caminho 1: painel de fatos)
//
// Deriva, de forma DETERMINÍSTICA, os FATOS do prontuário em que a análise geral
// se apoia — a partir da MESMA coleta que alimenta a IA (coletarDadosCompletos),
// para o painel refletir exatamente o que ela viu. Puro (sem React, sem I/O) →
// testável. NÃO toca a geração nem o texto da análise; é só transparência.
//
// Escopo do MVP: Laboratório (última coleta) + Marcadores/autoanticorpos (última
// coleta). Diretrizes recuperadas (RAG) ficam para a fase 2 (hoje os trechos são
// descartados após o prompt).
// ============================================================

import {
  ExameLaboratorialAnonimizado,
  MarcadorAnonimizado,
} from "../types";

export interface ItemProveniencia {
  rotulo: string;
  valor: string;
}

export interface GrupoProveniencia {
  titulo: string; // ex.: "Laboratório"
  aba: string; // origem no prontuário (para o médico saber onde conferir)
  data: string | null; // DD/MM/AAAA da coleta (null se sem data)
  itens: ItemProveniencia[];
}

// Consulta anonimizada — só os campos que a proveniência exibe (B229 fase 2b).
export interface ConsultaProveniencia {
  data?: string;
  queixaPrincipal?: string;
  hma?: string;
  conduta?: string;
}

export interface DadosProveniencia {
  examesLab?: ExameLaboratorialAnonimizado[];
  marcadores?: MarcadorAnonimizado[];
  // B229 fase 2b (AI_148): medicações em uso + consultas recentes — o MESMO campo
  // e as MESMAS consultas anonimizadas que a IA lê (transparência, não é dado novo).
  medicacoesEmUso?: string;
  consultas?: ConsultaProveniencia[];
}

// Converte ISO ou BR (DD/MM/AAAA) em Date; null se inválida. Espelha o parse
// tolerante usado no pipeline (B217).
export function parseData(d?: string | null): Date | null {
  if (!d) return null;
  const s = String(d).trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  // ISO só-data (YYYY-MM-DD, sem hora) → ancorar ao MEIO-DIA local, senão
  // `new Date("2026-07-10")` é UTC-meia-noite e recua um dia no fuso BR (AI_100).
  const isoDataPura = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const dt = br
    ? new Date(`${br[3]}-${br[2]}-${br[1]}T12:00:00`)
    : new Date(isoDataPura ? `${s}T12:00:00` : s);
  return isNaN(dt.getTime()) ? null : dt;
}

export function fmtData(d?: string | null): string | null {
  const dt = parseData(d);
  if (!dt) return null;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${dt.getFullYear()}`;
}

// Item de valor "presente" = não vazio, não placeholder.
function temValor(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s !== "" && s.toLowerCase() !== "n/a" && s.toLowerCase() !== "undefined";
}

// Rótulos amigáveis dos marcadores (chave do Firebase → nome clínico). Chaves
// fora deste mapa NÃO entram (evita expor campos meta/legados).
export const ROTULOS_MARCADORES: Record<string, string> = {
  afp: "AFP",
  ca199: "CA 19-9",
  "ca19-9": "CA 19-9",
  cea: "CEA",
  ca125: "CA 125",
  psa: "PSA",
  ferro: "Ferro",
  ferritina: "Ferritina",
  saturacaoTransferrina: "Saturação de transferrina",
  cobreSerico: "Cobre sérico",
  cobreUrinario: "Cobre urinário",
  ceruloplasmina: "Ceruloplasmina",
  alfa1Antitripsina: "Alfa-1-antitripsina",
  clearanceCreatinina: "Clearance de creatinina",
  clearanceCreatininaCorrigido: "Clearance corrigido (1,73m²)",
  proteinuria24h: "Proteinúria 24h",
  sodioUrinarioAmostra: "Sódio urinário (amostra)",
  sodioUrinario24h: "Sódio urinário 24h",
  iggSerico: "IgG sérico",
  igg4: "IgG4",
  efpAlbumina: "Albumina (eletroforese)",
  efpGama: "Gama (eletroforese)",
};

// Escolhe o registro mais recente de uma lista por um extrator de data.
function maisRecente<T>(
  itens: T[] | undefined,
  getData: (t: T) => string | undefined
): T | null {
  if (!Array.isArray(itens) || itens.length === 0) return null;
  let melhor: T | null = null;
  let melhorMs = -Infinity;
  for (const it of itens) {
    const dt = parseData(getData(it));
    const ms = dt ? dt.getTime() : -Infinity;
    // Sem data válida vale só se nada melhor apareceu (mantém determinístico:
    // o primeiro sem-data ganha o piso, um COM data sempre o supera).
    if (ms > melhorMs || (melhor === null && ms === -Infinity)) {
      melhor = it;
      melhorMs = ms;
    }
  }
  return melhor;
}

/**
 * Deriva os grupos de proveniência a partir da coleta que a IA usou.
 * Retorna [] quando não há fato nenhum (o painel então não é mostrado).
 */
export function derivarProveniencia(
  dados: DadosProveniencia
): GrupoProveniencia[] {
  const grupos: GrupoProveniencia[] = [];

  // 1. LABORATÓRIO — última coleta (todos os valores presentes; são exatamente
  //    os que a IA leu daquele exame).
  const ultimoLab = maisRecente(dados.examesLab, (e) => e.data);
  if (ultimoLab && ultimoLab.valores) {
    const itens: ItemProveniencia[] = Object.entries(ultimoLab.valores)
      .filter(([, v]) => temValor(v))
      .map(([k, v]) => ({ rotulo: k, valor: String(v) }));
    if (itens.length > 0) {
      grupos.push({
        titulo: "Laboratório",
        aba: "Exames de Laboratório",
        data: fmtData(ultimoLab.data),
        itens,
      });
    }
  }

  // 2. MARCADORES / AUTOANTICORPOS — última coleta.
  const ultimoMarc = maisRecente(
    dados.marcadores,
    (m) => m.dataColeta || m.data
  );
  if (ultimoMarc) {
    const itens: ItemProveniencia[] = [];
    for (const [chave, rotulo] of Object.entries(ROTULOS_MARCADORES)) {
      const v = (ultimoMarc as Record<string, unknown>)[chave];
      if (temValor(v)) itens.push({ rotulo, valor: String(v) });
    }
    if (itens.length > 0) {
      grupos.push({
        titulo: "Marcadores e autoanticorpos",
        aba: "Marcadores",
        data: fmtData(ultimoMarc.dataColeta || ultimoMarc.data),
        itens,
      });
    }
  }

  // 3. MEDICAÇÕES EM USO — o mesmo campo `medicacoesEmUso` que a IA lê (B229 fase 2b).
  if (temValor(dados.medicacoesEmUso)) {
    const meds = splitMedicacoes(String(dados.medicacoesEmUso));
    if (meds.length > 0) {
      grupos.push({
        titulo: "Medicações em uso",
        aba: "Cadastro / Avaliação clínica",
        data: null,
        itens: meds.map((m) => ({ rotulo: "", valor: m })),
      });
    }
  }

  // 4. CONSULTAS RECENTES — as 3 últimas, resumidas (data + queixa/conduta).
  if (Array.isArray(dados.consultas) && dados.consultas.length > 0) {
    const recentes = dados.consultas
      .slice()
      .sort(
        (a, b) =>
          (parseData(b.data)?.getTime() ?? -Infinity) -
          (parseData(a.data)?.getTime() ?? -Infinity)
      )
      .slice(0, 3);
    const itens: ItemProveniencia[] = recentes
      .map((c) => ({ rotulo: fmtData(c.data) || "sem data", valor: resumoConsulta(c) }))
      .filter((it) => it.valor.length > 0);
    if (itens.length > 0) {
      grupos.push({
        titulo: "Consultas recentes",
        aba: "Consultas / Evolução",
        data: null,
        itens,
      });
    }
  }

  return grupos;
}

// Separa a string de medicações em itens. Usa quebra de linha/";" quando houver
// (formato mais confiável); só cai na vírgula se não houver esses delimitadores
// (evita partir doses como "20+15+10").
export function splitMedicacoes(s: string): string[] {
  const str = String(s || "").trim();
  if (!str) return [];
  const delim = /[\n;]/.test(str) ? /[\n;]+/ : /,/;
  return str
    .split(delim)
    .map((x) => x.trim())
    .filter(Boolean);
}

// Resumo curto de uma consulta para o painel (não a evolução inteira).
function resumoConsulta(c: ConsultaProveniencia): string {
  const partes: string[] = [];
  const q = c.queixaPrincipal || c.hma;
  if (temValor(q)) partes.push(String(q).trim());
  if (temValor(c.conduta)) partes.push(`Conduta: ${String(c.conduta).trim()}`);
  const s = partes.join(" · ").replace(/\s+/g, " ");
  return s.length > 180 ? `${s.slice(0, 179)}…` : s;
}
